import './style.css';
import { injectThemeVariables, tokens } from '@ui/designTokens';
import { globalRouter } from './router';
import { AppShell } from '@ui/shell';
import { ClaweyHud } from '@ui/components/claweyHud';
import { SpellingReef } from '@ui/components/spellingReef';
import { ClueHunterStation } from '@stations/comprehension/clueHunter';
import { ContextLensStation } from '@stations/comprehension/contextLens';
import { FactDiverStation } from '@stations/comprehension/factDiver';
import { QuestEngine } from '@stations/sprint/questEngine';
import { MockExamStation } from '@stations/mock/mockExam';
import { ScannerStation } from '@stations/scanner/scannerStation';
import { GeometryStation } from '@stations/geometry/geometryStation';
import { GalleryStation } from '@stations/gallery/galleryStation';
import { SessionGuard } from '@storage/sessionGuard';
import { SrsScheduler } from '@storage/srsScheduler';
import { DailyReviewComponent } from '@ui/components/dailyReview';
import { ReadinessRadar } from '@ui/components/readinessRadar';
import { FatigueChart } from '@ui/components/fatigueChart';
import { OceanCanvas } from '@webgl/oceanCanvas';
import { renderCornerFiligree, renderCompassWatermark } from '@ui/components/ornateHelpers';
import { ProfileManager } from '@storage/profileManager';
import { AtelierTour } from '@ui/components/atelierTour';
import { TideAdvisor } from '@ui/components/tideAdvisor';
import { FeedbackLogbook } from './feedback/feedbackLogbook';
import { StationProgression } from '@storage/stationProgression';
import { RecentWorkDeck } from '@ui/components/recentWorkDeck';
import { AuthManager } from './auth/authManager';
import { SyncEngine } from '@storage/syncEngine';
import { ParentLockModal } from '@ui/components/parentLockModal';
import { AtelierEntranceGate } from '@ui/components/atelierEntranceGate';

// 0. Initialize Profile Manager, Auth Manager, and Cross-Device Sync (FR-PROF-001, FR-SYNC-001)
ProfileManager.init();
AuthManager.init();
SyncEngine.init();

// 1. Inject Theme Variables onto :root
injectThemeVariables();

// 2. Initialize Three.js WebGL Atmospheric Background
new OceanCanvas(document.body);

// 3. Service Worker Registration for PWA & Offline Support (NFR-PWA-001)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('Service worker registration failed:', err);
    });
  });
}

// 4. Initialize Clawey Mascot HUD (Accessible to all stations)
const claweyHud = new ClaweyHud();
document.body.appendChild(claweyHud.getElement());

// 5. Register Station Routes
interface HomeStationMeta {
  id: string;
  progressionKeys: string[];
  route: string;
  emblem: string;
  domainTag: string;
  domainTagClass?: string;
  title: string;
  desc: string;
  defaultCta: string;
  featured?: boolean;
}

const HOME_STATIONS: HomeStationMeta[] = [
  {
    id: 'mock',
    progressionKeys: ['mock'],
    route: '#/mock',
    emblem: '🏛️',
    domainTag: 'Bexley 11+ · 100 Qs',
    title: 'Full Mock Examination',
    desc: 'Paper 1 (50 min), 10-min restorative break & Paper 2 (50 min) with OMR bubble answer sheet and SAS scoring.',
    defaultCta: 'Enter Mock Exam &rarr;',
  },
  {
    id: 'comprehension',
    progressionKeys: ['comprehension'],
    route: '#/read',
    emblem: '📖',
    domainTag: 'Domain 2d',
    title: 'English Comprehension',
    desc: 'Master KS2 Inference with ≤60-word passage cards, sentence proof taps, and 10 progressive challenge tiers.',
    defaultCta: 'Enter Station &rarr;',
  },
  {
    id: 'spelling',
    progressionKeys: ['spelling'],
    route: '#/spelling',
    emblem: '🐚',
    domainTag: 'SPAG · Nessy + TbT',
    domainTagClass: 'coral',
    title: 'Spelling Reef',
    desc: 'Multisensory spelling: syllable chunking, nonsense-word decoding, visual mnemonics & morphology.',
    defaultCta: 'Dive Into Spelling &rarr;',
    featured: true,
  },
  {
    id: 'sprint',
    progressionKeys: ['sprint'],
    route: '#/sprint',
    emblem: '⚡',
    domainTag: 'Quest Speed',
    domainTagClass: 'brass',
    title: 'Bexley Quest Sprints',
    desc: 'Paper 1 (Verbal & English) & Paper 2 (Maths & NVR) micro-rounds with visual timer and Zen Mode.',
    defaultCta: 'Start Micro-Sprint &rarr;',
  },
  {
    id: 'context-lens',
    progressionKeys: ['context-lens'],
    route: '#/context-lens',
    emblem: '🔎',
    domainTag: 'Domain 2a & 2g',
    domainTagClass: 'brass',
    title: 'Context Lens',
    desc: 'Marine vocabulary & nuance with tactile naturalist definition drawer & 2-question mastery checks.',
    defaultCta: 'Explore Lexicon &rarr;',
  },
  {
    id: 'fact-diver',
    progressionKeys: ['fact-diver'],
    route: '#/fact-diver',
    emblem: '🤿',
    domainTag: 'Domain 2b',
    title: 'Fact Diver',
    desc: 'Rapid fact retrieval across 10 non-fiction depth zones with optional 60s challenge and Zen mode.',
    defaultCta: 'Dive for Facts &rarr;',
  },
  {
    id: 'geometry',
    progressionKeys: ['geometry-angles', 'geometry-mirror'],
    route: '#/geometry',
    emblem: '📐',
    domainTag: 'Tactile 3D',
    title: 'Geometry Sandbox',
    desc: 'Claw Protractor, Benthic Mirror Line, and Ocean Sonar with 10 progressive angle & reflection tiers.',
    defaultCta: 'Explore Sandbox &rarr;',
  },
  {
    id: 'scan',
    progressionKeys: ['scan'],
    route: '#/scan',
    emblem: '📷',
    domainTag: 'Misconception AI',
    domainTagClass: 'coral',
    title: 'Schoolwork Scanner',
    desc: 'Upload worksheets, diagnose KS2 error patterns & master 10 progressive adaptive remediation quests.',
    defaultCta: 'Scan Worksheet &rarr;',
  },
];

function renderStationCard(meta: HomeStationMeta): string {
  let completedCount = 0;
  let maxUnlockedSet = 1;
  let currentSet = 1;
  let tier: 'none' | 'bronze' | 'silver' | 'gold' = 'none';

  for (const key of meta.progressionKeys) {
    const prog = StationProgression.getProgression(key);
    const count = Object.keys(prog.completedSets).length;
    completedCount += count;
    if (prog.maxUnlockedSet > maxUnlockedSet) maxUnlockedSet = prog.maxUnlockedSet;
    if (prog.currentSet > currentSet) currentSet = prog.currentSet;
    const seal = StationProgression.getStationMasterySeal(key);
    if (seal.tier === 'gold') tier = 'gold';
    else if (seal.tier === 'silver' && tier !== 'gold') tier = 'silver';
    else if (seal.tier === 'bronze' && tier === 'none') tier = 'bronze';
  }

  // Geometry aggregates 2 sets of 10 (20 total), normalize to 10 scale for visual pills
  const normalizedCompleted = meta.progressionKeys.length > 1
    ? Math.round(completedCount / meta.progressionKeys.length)
    : completedCount;

  const isMastered = normalizedCompleted >= 10;
  const inProgress = completedCount > 0 && !isMastered;

  let cardModifierClass = '';
  if (isMastered) cardModifierClass = 'station-card-gold-mastery';
  else if (inProgress) cardModifierClass = 'station-card-in-progress';
  else if (meta.featured) cardModifierClass = 'featured-coral';

  let ctaText = meta.defaultCta;
  let btnClass = meta.featured ? 'btn-coral' : 'btn-vellum';

  if (isMastered) {
    ctaText = '👑 Mastered (Replay Sets) &rarr;';
    btnClass = 'btn-brass station-btn-mastered';
  } else if (inProgress) {
    ctaText = `Continue Set ${currentSet} &rarr;`;
    btnClass = 'btn-coral station-btn-active';
  }

  const sealLabel = tier === 'gold' ? 'Gilded Gold' : tier === 'silver' ? 'Silver' : 'Bronze';
  const sealIcon = tier === 'gold' ? '🥇' : tier === 'silver' ? '🥈' : '🥉';

  return `
    <article class="vellum-surface station-card card-interactive ${cardModifierClass}">
      ${renderCornerFiligree()}
      ${renderCompassWatermark()}
      <div style="position: relative; z-index: 2;">
        <div class="station-card-top">
          <div class="station-emblem-wrap">
            <div class="station-emblem">${meta.emblem}</div>
            ${tier !== 'none' ? `
              <div class="station-wax-seal seal-${tier}" title="${sealLabel} Victorian Mastery Seal">
                <span>${sealIcon}</span>
                <span>${sealLabel}</span>
              </div>
            ` : ''}
          </div>
          <div class="station-card-tags">
            ${completedCount > 0 ? `
              <span class="station-set-badge ${isMastered ? 'badge-mastered' : 'badge-progress'}">
                🧭 Set ${Math.min(10, currentSet)}/10
              </span>
            ` : ''}
            <span class="domain-tag ${meta.domainTagClass ?? ''}">${meta.domainTag}</span>
          </div>
        </div>

        <h3 class="station-title">${meta.title}</h3>
        <p class="station-desc">${meta.desc}</p>

        <div class="station-progress-deck">
          <div class="station-progress-header">
            <span class="station-progress-label">
              ${completedCount === 0 
                ? '10 Challenge Sets Available' 
                : isMastered 
                  ? '⭐ 10/10 Sets Mastered!' 
                  : `⚡ ${completedCount} Set${completedCount === 1 ? '' : 's'} Completed`}
            </span>
            <span class="station-star-tally">
              ${completedCount > 0 ? `★ ${completedCount}` : '🧭 Unstarted'}
            </span>
          </div>
          <div class="station-set-pills-row" role="progressbar" aria-valuenow="${normalizedCompleted}" aria-valuemin="0" aria-valuemax="10">
            ${Array.from({ length: 10 }, (_, i) => {
              const setNum = i + 1;
              const isSetDone = setNum <= normalizedCompleted;
              const isSetCurrent = setNum === currentSet;
              const isSetUnlocked = setNum <= maxUnlockedSet;
              let statusClass = 'pill-locked';
              if (isSetDone) statusClass = 'pill-completed';
              else if (isSetCurrent) statusClass = 'pill-current';
              else if (isSetUnlocked) statusClass = 'pill-unlocked';
              return `<span class="station-mini-pill ${statusClass}" title="Set ${setNum}: ${isSetDone ? 'Mastered' : isSetUnlocked ? 'Unlocked' : 'Locked'}"></span>`;
            }).join('')}
          </div>
        </div>
      </div>

      <a href="${meta.route}" class="touch-target ${btnClass}" style="width: 100%; position: relative; z-index: 2;">
        ${ctaText}
      </a>
    </article>
  `;
}

function renderAllStationCards(): string {
  return HOME_STATIONS.map((s) => renderStationCard(s)).join('\n');
}

globalRouter
  .register('#/', 'Expedition Map', () => {
    const container = document.createElement('div');
    container.innerHTML = `
      <section class="vellum-surface hero-banner">
        ${renderCornerFiligree()}
        ${renderCompassWatermark()}
        <div style="position: relative; z-index: 2;">
          <div class="atelier-eyebrow-pill">
            <span>🌊</span>
            <span>VICTORIAN DEEP-SEA ATELIER</span>
          </div>
          <h2 class="hero-title">The Rockpool Shallows</h2>
          <p class="hero-lead">
            Welcome, Tudor! Inspect genuine 3D marine specimens, chart coordinate courses, and prepare for the Bexley 11+ in your interactive marine laboratory.
          </p>
          <div class="hero-cta-row">
            <a href="#/read" class="touch-target btn-coral">📖 Start Clue Hunter</a>
            <a href="#/sprint" class="touch-target btn-brass">⚡ 10-Minute Quest Sprint</a>
            <a href="#/mock" class="touch-target btn-vellum" style="width: auto;">🏛️ Full Bexley Mock</a>
            <button id="hero-tour-btn" class="touch-target btn-vellum" style="width: auto; cursor: pointer;">🧭 Field Guide &amp; Tour</button>
          </div>
        </div>

        <!-- 2D Victorian Naturalist Showcase Plate (Replaces the 3D WebGL Turntable in Hero) -->
        <div class="atelier-hero-plate" style="position: relative; z-index: 2;">
          <img 
            src="/assets/atelier/brass-diving-helmet.png" 
            alt="Victorian Deep-Sea Diving Apparatus" 
            class="atelier-engraving-blend"
          />
          <div style="font-family: var(--font-heading); font-size: 0.95rem; font-weight: 800; color: var(--morris-indigo); margin-top: 10px; text-align: center;">
            H.M.S. Challenger Diving Apparatus
          </div>
          <div style="font-size: 0.8rem; color: #555; font-style: italic; text-align: center; margin-top: 2px;">
            Victorian Deep-Sea Atelier &amp; Field Guide
          </div>
        </div>
      </section>

      <!-- Context-Aware Victorian Chronometer & Tide Advisor (FR-REC-001..003) -->
      <div id="home-tide-advisor-mount"></div>

      <section class="station-grid" id="home-station-grid">
        ${renderAllStationCards()}
      </section>

      <!-- Tudor's Recent Work Logbook Deck -->
      <div id="home-recent-work-mount" style="margin-top: 32px;"></div>
    `;

    // Mount Field Guide Tour Button
    const tourBtn = container.querySelector('#hero-tour-btn');
    tourBtn?.addEventListener('click', () => {
      new AtelierTour().show();
    });

    // Mount Tide Advisor Component (FR-REC-001..003)
    const tideMount = container.querySelector('#home-tide-advisor-mount');
    if (tideMount) {
      const tideAdvisor = new TideAdvisor();
      tideMount.appendChild(tideAdvisor.getElement());
    }

    // Mount Recent Work Deck on Home
    const homeRecentMount = container.querySelector<HTMLElement>('#home-recent-work-mount');
    if (homeRecentMount) {
      const homeDeck = new RecentWorkDeck();
      homeRecentMount.appendChild(homeDeck.getElement());
    }

    // Reactive progression change re-rendering
    const stationGrid = container.querySelector<HTMLElement>('#home-station-grid');
    if (stationGrid) {
      StationProgression.onProgressionChange(() => {
        if (document.body.contains(stationGrid)) {
          stationGrid.innerHTML = renderAllStationCards();
        }
      });
    }

    return container;
  })
  .register('#/read', 'Comprehension Station', () => {
    const station = new ClueHunterStation(claweyHud);
    return station.render();
  })
  .register('#/context-lens', 'Context Lens: Vocabulary', () => {
    const station = new ContextLensStation(claweyHud);
    return station.render();
  })
  .register('#/fact-diver', 'Fact Diver: Rapid Retrieval', () => {
    const station = new FactDiverStation(claweyHud);
    return station.render();
  })
  .register('#/spelling', 'Spelling Reef', () => {
    const reef = new SpellingReef(claweyHud);
    return reef.render();
  })
  .register('#/daily-review', 'Daily Memory Review', () => {
    const review = new DailyReviewComponent({ hud: claweyHud });
    return review.render();
  })
  .register('#/sprint', 'Quest Exam Sprint', () => {
    const engine = new QuestEngine(claweyHud);
    return engine.render();
  })
  .register('#/mock', 'Full Bexley Mock Exam', () => {
    const station = new MockExamStation({ hud: claweyHud });
    return station.render();
  })
  .register('#/geometry', 'Tactile Geometry', () => {
    const station = new GeometryStation(claweyHud);
    return station.render();
  })
  .register('#/scan', 'Schoolwork Scanner', () => {
    const station = new ScannerStation({ hud: claweyHud });
    return station.render();
  })
  .register('#/dashboard', 'Parent Dashboard', () => {
    // Access Guard: Ensure parent is authenticated before rendering dashboard
    if (!AuthManager.isParentAuthenticated()) {
      const gateContainer = document.createElement('div');
      gateContainer.className = 'morris-card';
      gateContainer.style.cssText = 'text-align: center; padding: 48px 24px; max-width: 540px; margin: 40px auto;';
      gateContainer.innerHTML = `
        <div style="font-size: 3rem; margin-bottom: 12px;">🔐</div>
        <h2 style="color: ${tokens.color.morrisIndigo};">Parent Observatory Locked</h2>
        <p style="color: #4A5B70; margin: 12px 0 24px; line-height: 1.5;">
          This sanctum tracks Tudor's Bexley 11+ readiness, cognitive pacing, and daily practice limits. Please authenticate to continue.
        </p>
        <button id="gate-unlock-btn" class="touch-target" style="padding: 12px 28px; background: ${tokens.color.morrisIndigo}; color: #FAF8F5; border: 2px solid ${tokens.color.gildedBrass}; border-radius: 12px; font-weight: 800; cursor: pointer;">
          Enter Parent Master Key &rarr;
        </button>
      `;

      const modal = new ParentLockModal({
        onSuccess: () => {
          globalRouter.navigate('#/dashboard');
        },
        onCancel: () => {
          globalRouter.navigate('#/');
        },
      });

      gateContainer.querySelector('#gate-unlock-btn')?.addEventListener('click', () => {
        modal.show();
      });

      // Prompt lock modal
      setTimeout(() => {
        if (!AuthManager.isParentAuthenticated()) {
          modal.show();
        }
      }, 40);

      return gateContainer;
    }

    const container = document.createElement('div');
    const currentLimit = SessionGuard.getDailyLimitMinutes();
    const todayMinutes = SessionGuard.getTodayPracticeMinutes();

    container.innerHTML = `
      <section class="morris-card" style="margin-bottom: 24px;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 8px;">
          <h2 style="margin: 0;">Parent Analytics &amp; Bexley Readiness Deck</h2>
          <button id="btn-lock-parent-deck" class="touch-target btn-vellum" style="padding: 6px 14px; font-size: 0.85rem; border-radius: 999px; cursor: pointer; border: 1.5px solid ${tokens.color.gildedBrass}; color: ${tokens.color.morrisIndigo}; font-weight: 700;">
            🔒 Lock Deck
          </button>
        </div>
        <p style="margin: 8px 0 24px;">Track Age-Standardised Score (SAS) projections, 4-axis Bexley readiness, ADHD cognitive fatigue curves, and daily practice limits.</p>
        
        <!-- ADHD Daily Practice Time Guard Controls (FR-ADHD-003, T-11.2.4) -->
        <div class="morris-card morris-card-gold" style="margin-bottom: 28px; padding: 24px;">
          <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px;">
            <div>
              <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase;">
                🛡️ Cognitive Health & ADHD Guard
              </div>
              <h3 style="font-size: 1.4rem; color: ${tokens.color.morrisIndigo}; margin: 6px 0;">
                Daily Practice Time Limit
              </h3>
              <p id="practice-status-text" style="font-size: 0.95rem; color: #3A4D66; margin: 0; max-width: 500px;">
                Tudor has practiced <strong>${todayMinutes} min</strong> today. When target is reached, Clawey suggests a calming rest break.
              </p>
            </div>

            <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
              <label for="select-daily-limit" style="font-weight: 700; font-size: 0.95rem; color: ${tokens.color.morrisIndigo};">Daily Limit:</label>
              <select id="select-daily-limit" class="touch-target" style="padding: 10px 16px; font-size: 1rem; font-weight: 700; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer;">
                ${[15, 20, 25, 30, 45, 60].map(m => `<option value="${m}" ${m === currentLimit ? 'selected' : ''}>${m} Minutes</option>`).join('')}
              </select>
              <button id="btn-reset-practice-today" class="touch-target" style="padding: 10px 14px; font-size: 0.85rem; border: 1px solid rgba(26,54,93,0.3); border-radius: 12px; background: #FFFDF9; cursor: pointer;">
                Reset Today
              </button>
            </div>
          </div>
        </div>

        <!-- 0. Tudor's Recent Expeditions & Work Archive -->
        <div id="recent-work-deck-mount" style="margin-bottom: 28px;"></div>

        <!-- 1. Bexley Readiness Radar Mount (FR-DASH-001, FR-DASH-002) -->
        <div id="radar-deck-mount" style="margin-bottom: 28px;"></div>

        <!-- 2. ADHD Fatigue & Cognitive Pacing Chart Mount (FR-DASH-003, FR-DASH-004) -->
        <div id="fatigue-deck-mount"></div>

        <!-- 3. Inspector's Logbook & Feedback Deck Mount -->
        <div id="feedback-logbook-mount"></div>
      </section>
    `;

    // Mount Recent Work Deck Component
    const recentMount = container.querySelector<HTMLElement>('#recent-work-deck-mount');
    if (recentMount) {
      const recentDeck = new RecentWorkDeck();
      recentMount.appendChild(recentDeck.getElement());
    }

    // Mount Radar Component
    const radarMount = container.querySelector<HTMLElement>('#radar-deck-mount');
    if (radarMount) {
      const radar = new ReadinessRadar();
      radarMount.appendChild(radar.render());
    }

    // Mount Fatigue Component
    const fatigueMount = container.querySelector<HTMLElement>('#fatigue-deck-mount');
    if (fatigueMount) {
      const fatigue = new FatigueChart();
      fatigueMount.appendChild(fatigue.render());
    }

    // Mount Feedback Logbook Component
    const logbookMount = container.querySelector<HTMLElement>('#feedback-logbook-mount');
    if (logbookMount) {
      const logbook = new FeedbackLogbook();
      logbookMount.appendChild(logbook.render());
    }

    const selectEl = container.querySelector<HTMLSelectElement>('#select-daily-limit');
    selectEl?.addEventListener('change', () => {
      const val = parseInt(selectEl.value, 10);
      SessionGuard.setDailyLimitMinutes(val);
    });

    const resetBtn = container.querySelector<HTMLButtonElement>('#btn-reset-practice-today');
    resetBtn?.addEventListener('click', () => {
      SessionGuard.resetTodayPracticeTime();
      const pText = container.querySelector<HTMLElement>('#practice-status-text');
      if (pText) {
        pText.innerHTML = `Tudor has practiced <strong>0 min</strong> today. When target is reached, Clawey suggests a calming rest break.`;
      }
    });

    const lockBtn = container.querySelector<HTMLButtonElement>('#btn-lock-parent-deck');
    lockBtn?.addEventListener('click', async () => {
      await AuthManager.logout();
      globalRouter.navigate('#/');
    });

    return container;
  })
  .register('#/gallery', 'Naturalist Studio & Gallery', () => {
    const station = new GalleryStation();
    return station.render();
  });

// 6. Mount AppShell
const appRoot = document.querySelector<HTMLDivElement>('#app');
if (appRoot) {
  const shell = new AppShell(appRoot, globalRouter);
  shell.init();

  // 6b. Site-Wide Family Entrance Gate (FR-AUTH-001)
  if (!AuthManager.isFamilyUnlocked()) {
    const entranceGate = new AtelierEntranceGate({
      onUnlock: () => {
        const dest = window.location.hash || '#/';
        globalRouter.navigate(dest);
      },
    });
    entranceGate.show();
  }

  // 7. Spaced Repetition Daily Review Gate on launch (FR-SRS-002)
  const currentHash = window.location.hash;
  if (currentHash === '' || currentHash === '#/' || currentHash === '#/daily-review') {
    if (SrsScheduler.hasDueItems() && !SrsScheduler.isSessionCompletedToday()) {
      globalRouter.navigate('#/daily-review');
    }
  }
}
